import { useEffect, useState, useCallback } from "react";
import { isSupported, onMessage } from "firebase/messaging";
import { toast } from "react-toastify";
import { postData } from "../apis/api";
import { getWebPushToken } from "../utils/webPushToken";
const VAPID = "BPsOvRfHF4q21FhyXqUCtKyW7XUnr0tEWDbo5Drh16HALWb9bgevNQ-GFh3B0ERx-Po__rqiE31pgqwwi4OLhio";
export async function listenForPush(handler) {
  if (!(await isSupported())) return () => {};
  const { messaging } = await import("../firebase");
  return onMessage(messaging, handler);
}
export default function useFcmRegistration(auth) {
  const [permission, setPermission] = useState(() => typeof Notification === "undefined" ? "unsupported" : Notification.permission);
  const enable = useCallback(async () => {
    if (typeof Notification !== "undefined") {
      try {
        const next = await Notification.requestPermission();
        setPermission(next);
        return next;
      } catch (_) {
        return Notification.permission;
      }
    }
    return "unsupported";
  }, []);
  useEffect(() => {
    if (!auth) return undefined;
    let stopped = false, pending = false, lastToken = "", lastRegistration = 0, reported = false;
    const register = async () => {
      if (pending || stopped || typeof Notification === "undefined") return;
      const currentPerm = Notification.permission;
      setPermission(currentPerm);
      if (currentPerm !== "granted") return;
      pending = true;
      try {
        if (!(await isSupported()) || stopped) return;
        const { app, messaging } = await import("../firebase");
        if (!messaging) return;
        const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
        const swReg = (navigator.serviceWorker && navigator.serviceWorker.ready)
          ? await navigator.serviceWorker.ready
          : registration;
        const token = await getWebPushToken(app, messaging, { vapidKey: VAPID, serviceWorkerRegistration: swReg || registration });
        if (!token) throw new Error("No web push token returned");
        if (!stopped && (token !== lastToken || Date.now() - lastRegistration > 3600000)) {
          await postData("notifications/fcmDeviceId", { fcmToken: token, platform: "web" });
          lastToken = token; lastRegistration = Date.now();
        }
      } catch (error) {
        if (!reported && !stopped) {
          reported = true;
          toast.error("Browser push registration failed. Reload and enable notifications to receive call alerts.");
          postData("aviate/JSError/", { type: "web-push-registration", log: { code: error.code || "registration-failed" } }).catch(() => {});
        }
      } finally { pending = false; }
    };
    register();
    const interval = setInterval(register, 60000);
    window.addEventListener("focus", register);
    document.addEventListener("visibilitychange", register);
    return () => { stopped = true; clearInterval(interval); window.removeEventListener("focus", register); document.removeEventListener("visibilitychange", register); };
  }, [auth, permission]);
  return { permission, enable };
}
